import React, { useState, useContext } from 'react';
import {
      CModal,
      CModalHeader,
      CModalTitle,
      CModalBody,
      CModalFooter,
      CButton,
      CForm,
      CFormInput,
      CFormLabel,
      CFormFeedback,
      CInputGroup,
      CInputGroupText,
      CSpinner,
      CAlert
} from '@coreui/react';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { AuthUserContext } from '../views/session';
import config from '../utils/EnvironmentSwitcher';
import Logger from '../utils/Logger';

/**
 * MierosTransferModal
 * 
 * Reusable modal for transferring Mieros tokens.
 * Can be used in checkout flow or admin panel.
 * 
 * Props:
 * - show: boolean - Whether to show the modal
 * - onHide: function - Callback when modal is closed
 * - onSuccess: function(transferData) - Callback when transfer succeeds
 * - recipientId: string (optional) - Pre-populated recipient ID (e.g., sellerId)
 * - amount: number|string (optional) - Pre-populated amount
 * - title: string (optional) - Custom modal title
 * - readOnly: boolean (optional) - If true, recipient and amount are read-only
 * - showBalance: boolean (optional) - Whether to show wallet balance
 */
const MierosTransferModal = ({
      show,
      onHide,
      onSuccess,
      recipientId: initialRecipientId = '',
      amount: initialAmount = '',
      title = 'Transfer MiEROS Tokens',
      readOnly = false,
      showBalance = true
}) => {
      const { authUser } = useContext(AuthUserContext);
      const auth = getAuth();
      const currentUser = auth.currentUser;

      const [recipientId, setRecipientId] = useState(initialRecipientId);
      const [amount, setAmount] = useState(initialAmount);
      const [isTransferring, setIsTransferring] = useState(false);
      const [walletBalance, setWalletBalance] = useState(null);
      const [errors, setErrors] = useState({});

      // Load initial values when props change
      React.useEffect(() => {
            if (initialRecipientId) setRecipientId(initialRecipientId);
            if (initialAmount) setAmount(initialAmount);
      }, [initialRecipientId, initialAmount]);

      // Fetch wallet balance when modal opens
      React.useEffect(() => {
            if (show && showBalance && authUser) {
                  fetchWalletBalance();
            }
      }, [show, showBalance, authUser]);

      const fetchWalletBalance = async () => {
            try {
                  const userId = currentUser?.uid || authUser?.uid;
                  if (!userId) return;

                  const token = await currentUser.getIdToken();
                  const response = await fetch(`${config.apiUrl}/api/balance/${userId}`, {
                        method: 'GET',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                        }
                  });

                  if (response.ok) {
                        const data = await response.json();
                        setWalletBalance(data.balance ?? 0);
                  }
            } catch (error) {
                  Logger.error('Error fetching wallet balance:', error);
            }
      };

      const validateForm = () => {
            const newErrors = {};
            
            if (!recipientId || recipientId.trim() === '') {
                  newErrors.recipientId = 'Recipient is required';
            }
            
            if (!amount || amount === '') {
                  newErrors.amount = 'Amount is required';
            } else {
                  const numAmount = Number(amount);
                  if (isNaN(numAmount) || numAmount <= 0) {
                        newErrors.amount = 'Amount must be greater than 0';
                  } else if (showBalance && walletBalance !== null && numAmount > walletBalance) {
                        newErrors.amount = `Insufficient balance. You have ${walletBalance} MiEROS`;
                  }
            }
            
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
      };

      const handleTransfer = async () => {
            if (!validateForm()) return;

            setIsTransferring(true);
            setErrors({});

            try {
                  const token = await currentUser.getIdToken();
                  const numAmount = Number(amount);
                  
                  const response = await fetch(`${config.apiUrl}/api/transfer`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                              fromUserId: authUser.uid,
                              toUserIdentifier: recipientId.trim(),
                              amount: numAmount,
                              memo: `Payment for NFT purchase`
                        })
                  });

                  const data = await response.json();

                  if (!response.ok) {
                        throw new Error(data.message || 'Transfer failed');
                  }

                  toast.success(`Successfully transferred ${numAmount} MiEROS!`);
                  
                  // Refresh balance
                  if (showBalance) {
                        await fetchWalletBalance();
                  }

                  // Call success callback
                  if (onSuccess) {
                        onSuccess({
                              transactionId: data.transactionId,
                              amount: numAmount,
                              recipientId: recipientId.trim(),
                              timestamp: new Date().toISOString()
                        });
                  }

                  // Reset form if not read-only
                  if (!readOnly) {
                        setRecipientId('');
                        setAmount('');
                  }
                  
                  // Close modal
                  onHide();

            } catch (error) {
                  Logger.error('Error transferring MiEROS:', error);
                  toast.error(`Transfer failed: ${error.message}`);
            } finally {
                  setIsTransferring(false);
            }
      };

      const handleClose = () => {
            if (!isTransferring) {
                  setErrors({});
                  onHide();
            }
      };

      return (
            <CModal visible={show} onClose={handleClose} size="lg">
                  <CModalHeader closeButton={!isTransferring}>
                        <CModalTitle>{title}</CModalTitle>
                  </CModalHeader>
                  <CModalBody>
                        {showBalance && walletBalance !== null && (
                              <CAlert color="info" className="mb-3">
                                    <strong>Your Balance:</strong> {walletBalance} MiEROS
                              </CAlert>
                        )}

                        <CForm>
                              <div className="mb-3">
                                    <CFormLabel>Recipient (Email or User ID) *</CFormLabel>
                                    <CInputGroup>
                                          <CInputGroupText>📧</CInputGroupText>
                                          <CFormInput
                                                type="text"
                                                placeholder="Recipient Email or User ID"
                                                value={recipientId}
                                                onChange={(e) => setRecipientId(e.target.value)}
                                                disabled={isTransferring || readOnly}
                                                invalid={!!errors.recipientId}
                                          />
                                    </CInputGroup>
                                    {errors.recipientId && (
                                          <CFormFeedback invalid>{errors.recipientId}</CFormFeedback>
                                    )}
                              </div>

                              <div className="mb-3">
                                    <CFormLabel>Amount (MiEROS Tokens) *</CFormLabel>
                                    <CInputGroup>
                                          <CInputGroupText>💰</CInputGroupText>
                                          <CFormInput
                                                type="number"
                                                placeholder="Amount to transfer"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                min="0.01"
                                                step="0.01"
                                                disabled={isTransferring || readOnly}
                                                invalid={!!errors.amount}
                                          />
                                          <CInputGroupText>MiEROS</CInputGroupText>
                                    </CInputGroup>
                                    {errors.amount && (
                                          <CFormFeedback invalid>{errors.amount}</CFormFeedback>
                                    )}
                              </div>
                        </CForm>
                  </CModalBody>
                  <CModalFooter>
                        <CButton
                              color="secondary"
                              onClick={handleClose}
                              disabled={isTransferring}
                        >
                              Cancel
                        </CButton>
                        <CButton
                              color="primary"
                              onClick={handleTransfer}
                              disabled={isTransferring || !recipientId || !amount}
                        >
                              {isTransferring ? (
                                    <>
                                          <CSpinner size="sm" className="me-2" />
                                          Transferring...
                                    </>
                              ) : (
                                    '🚀 Transfer MiEROS'
                              )}
                        </CButton>
                  </CModalFooter>
            </CModal>
      );
};

export default MierosTransferModal;
